/**
 * ENG-4705: Channel input watchdog.
 *
 * Symptom: an inbound Slack/Telegram/Direct-Chat message lands in the Claude
 * Code TUI input buffer (visible as `❯ <text>` between the input-box rule
 * lines) but the channel server's auto-submit doesn't fire — the text just
 * sits there until something sends Enter manually.
 *
 * The dispatcher pattern landed in ENG-4684 reduces the *frequency* (slow
 * requests get fanned out to a background subagent so the parent's listener
 * turn returns immediately), but doesn't fix the underlying race: when
 * triage decides a request is "fast" and the parent handles it inline, the
 * main turn still occupies the TUI for several seconds, and any channel
 * message that arrives during that window stacks in the input buffer
 * un-submitted.
 *
 * Workaround the ticket itself documents: `tmux send-keys -t <session>
 * Enter`. This watchdog automates that — every poll cycle it captures the
 * pane for each managed claude-code agent, looks for un-submitted text in
 * the input box, and fires Enter once the same text has been sitting there
 * unchanged for STUCK_THRESHOLD_MS.
 *
 * Safety:
 *  - Skip the agent if a tmux client is attached (a human might be typing).
 *  - Require the text to be unchanged for STUCK_THRESHOLD_MS — short windows
 *    avoid racing the channel server's own (eventual) submit.
 *  - Bounded retries per stuck buffer (ENG-6017): up to MAX_ENTER_FIRES
 *    Enters, each spaced by the stuck threshold, then a loud give-up log.
 *    (The original single-shot design is exactly what stranded koda on
 *    2026-06-04: the one Enter went into Claude Code's session-feedback
 *    dialog, the buffer was marked resolved, and an operator Slack DM sat
 *    unsubmitted for 40+ minutes while every health metric stayed green.)
 *  - Dialog-aware (ENG-6017): if a recognised dialog overlays the pane,
 *    dismiss it via sweepDialogs() instead of firing Enter into it. The
 *    Enter budget is not consumed by dialog dismissals. Unknown dialogs
 *    are default-deny — never keyed.
 *  - ENG-6057: fires even while Claude is rendering a spinner. The old
 *    spinner gate is why the kylie 2026-06-05 stuck inputs were never
 *    healed — the agent stayed busy and the gate held on every poll.
 *    Claude Code queues input typed during a running turn, so the Enter
 *    safely submits the queued text; busy-state is a log annotation now.
 *  - ENG-6057: a lone "…" (U+2026) input is a CC render artifact, ignored —
 *    the fleet's entire historical fire count was Enters at this phantom.
 */

import {
  sweepDialogs,
  simpleTextHash,
  isUnanswerableUsageLimitDialog,
  isUnclaimedConfirmDialog,
  focusedConfirmDialogBlock,
  findUsageLimitResetHint,
  type DialogAction,
} from './claude-dialogs.js';

/**
 * ENG-9006 criterion 3: how long an UNCLAIMED focused dialog may sit on a pane
 * before we say so.
 *
 * Two minutes, chosen against the two costs. Too short and every legitimate
 * momentary modal pages; too long and we are back to "found on a screenshot",
 * which is the status quo this replaces. The manager polls every ~10-30s, so
 * 120s is 4-12 confirmations that the same block is still there — and a modal
 * a human is actively answering changes the block (or clears it) well inside
 * that window.
 */
const UNCLAIMED_DIALOG_ALERT_MS = 120_000;

const STUCK_THRESHOLD_MS = 5_000;
// ENG-6017: how many Enters we'll fire at one unchanged stuck buffer before
// giving up loudly. Each retry waits another stuckThreshold after the last.
const MAX_ENTER_FIRES = 3;
// ENG-6055: the "disturb" heal sequence — type a char, delete it, submit.
// On the kylie 2026-06-05 incident a bare Enter failed to submit TWICE
// (the same keystroke this watchdog fires) while x→BSpace→Enter submitted
// immediately: the TUI input handler was in a state where a standalone CR
// is swallowed but a real keystroke wakes it. Empirical against the current
// Claude Code / tmux pairing (root-cause tracked in ENG-6057), hence the
// `healMode` escape hatch back to bare Enters.
export const DISTURB_HEAL_KEYS = ['x', 'BSpace', 'Enter'] as const;
// One send-keys per key (never batched into one bracketed paste — see
// defaultArmSender in persistent-session.ts) with a small gap so the TUI
// processes each keystroke individually.
export const DISTURB_INTER_KEY_DELAY_MS = 200;

export type WatchdogHealMode = 'disturb' | 'bare';
// ENG-4716: when a tmux client is attached we don't skip the agent
// outright (the original safety-first carve-out swallowed the steady-
// state "monitoring" case operators care about). Instead we widen the
// stable-buffer window so a human typing has more than 5s to finish
// before the watchdog steps in. Active typing changes the buffer hash
// every keystroke and keeps resetting `firstSeenAt`, so this only
// matters when the buffer is *truly* stable.
const ATTACHED_STUCK_THRESHOLD_MS = 15_000;
const INPUT_BOX_DIVIDER = /^[─━]{10,}/;
const PROMPT_PREFIX = '❯ ';

export interface AgentInputState {
  /** Hash of the input-box text observed last poll. */
  lastInputHash: string;
  /** Wall-clock ms when this hash was first seen. */
  firstSeenAt: number;
  /** ENG-6017: how many Enters we've fired at this hash so far. */
  fires: number;
  /** Wall-clock ms of the most recent Enter fire (0 = never). */
  lastFireAt: number;
  /** Have we already emitted the give-up log line for this hash? */
  gaveUpLogged: boolean;
}

export interface WatchdogIo {
  /** Snapshot of the agent's tmux pane (multiline). Empty / null if the session doesn't exist. */
  capturePane: (codeName: string) => string | null;
  /** Whether any tmux client is attached to the session right now. */
  isClientAttached: (codeName: string) => boolean;
  /** Send a single Enter keystroke to the agent's tmux session. */
  sendEnter: (codeName: string) => void;
  /**
   * ENG-6017: send a dialog-dismissal key sequence (one send-keys call per
   * key, `interKeyDelayMs` apart) to the agent's tmux session.
   */
  sendKeys: (codeName: string, keys: readonly string[], interKeyDelayMs: number) => void;
  /** Logger. */
  log: (msg: string) => void;
  /** Current wall-clock ms (injectable for tests). */
  now: () => number;
  /**
   * ENG-6058: persist a give-up signal for the agent's channel servers.
   * Called exactly once per exhausted heal budget (alongside the GIVING UP
   * log); the channel servers' periodic sweeps turn it into a throttled
   * user-facing "please resend" notice for every conversation with an
   * undrained pending-inbound marker. Optional so tests and older callers
   * compose without it.
   */
  signalGiveUp?: (codeName: string) => void;
  /**
   * ENG-9006: the agent is parked on Claude Code's usage-limit modal — either
   * we just answered it, or we recognised it and refused to answer.
   *
   * This exists because the reactive usage-limit notice (ENG-8201) CANNOT fire
   * in this state. That path dispatches a turn, reads the transcript, and
   * answers the user when Claude Code records a `rate_limit` / 429 refusal. On
   * a modal-blocked pane no turn is ever attempted, so no refusal is ever
   * recorded, so nobody is ever told — the message just sits in the input box.
   * The ENG-9006 capture shows the consequence: four messages from one customer
   * queued behind the modal, unanswered, the customer repeating themselves.
   *
   * `resetsHint` is the reset moment verbatim off the modal when it named one
   * ("Aug 21, 10am"), else null. Optional so tests and older callers compose
   * without it; best-effort, never fails the poll cycle.
   */
  signalUsageLimit?: (codeName: string, resetsHint: string | null) => void;
}

export interface WatchdogConfig {
  /** ms a stuck buffer must persist before we fire Enter. Defaults to STUCK_THRESHOLD_MS. */
  stuckThresholdMs?: number;
  /**
   * ms a stuck buffer must persist before we fire Enter when a tmux
   * client is attached to the session. Higher than `stuckThresholdMs`
   * to give a human typer extra headroom. Defaults to
   * ATTACHED_STUCK_THRESHOLD_MS.
   */
  attachedStuckThresholdMs?: number;
  /**
   * ENG-6017: max Enters fired at one unchanged stuck buffer before the
   * loud give-up log. Defaults to MAX_ENTER_FIRES.
   */
  maxEnterFires?: number;
  /**
   * ENG-6055: heal keystroke escalation. 'disturb' (default) fires a bare
   * Enter on the first attempt, then the x→BSpace→Enter disturb sequence
   * for the remaining budget (a bare Enter is exactly the keystroke the
   * stuck TUI state eats — kylie 2026-06-05). 'bare' keeps every attempt
   * a single Enter (AGT_INPUT_HEAL_MODE=bare escape hatch).
   */
  healMode?: WatchdogHealMode;
  /**
   * ENG-9006 criterion 3: ms an UNCLAIMED focused dialog must persist before
   * the watchdog says so. Defaults to UNCLAIMED_DIALOG_ALERT_MS.
   */
  unclaimedDialogAlertMs?: number;
}

/**
 * ENG-6055: pick the keystroke(s) for a given fire attempt (1-based).
 * Attempt 1 is always a bare Enter — it's free, and on healthy hiccups it
 * works. From attempt 2 the bare Enter has demonstrably failed once, so
 * 'disturb' mode escalates to the x→BSpace→Enter sequence. Pure and
 * exported for unit tests.
 */
export function selectFireKeys(
  attempt: number,
  healMode: WatchdogHealMode = 'disturb',
): readonly string[] {
  if (healMode === 'disturb' && attempt >= 2) return DISTURB_HEAL_KEYS;
  return ['Enter'];
}

/**
 * Single-agent decision step — pure given the pane text + state. Returns
 * the next state and the action to take: dismiss a dialog, fire Enter,
 * emit the one-shot give-up log, or nothing.
 *
 * Exported for unit testing.
 */
export function decide(
  pane: string,
  prev: AgentInputState | undefined,
  now: number,
  config: WatchdogConfig = {},
): {
  fire: boolean;
  /** ENG-6017: a recognised dialog overlays the pane — dismiss it instead
   * of firing Enter into it. The Enter budget is untouched. */
  dialog?: DialogAction;
  /** ENG-6017: the Enter budget for this hash is exhausted — emit the
   * give-up log exactly once. */
  gaveUp?: boolean;
  /** ENG-8213: a recognised dialog is on screen that we deliberately will
   * NOT answer, and no fallback may key the pane either. Carries the
   * greppable reason for the caller to log. */
  blockedDialog?: string;
  /**
   * ENG-9006 criterion 3: a focused confirm-footer dialog that NO handler
   * claimed. A hash of the modal's own block, so the caller can tell "the
   * same unclaimed modal is still there" from "a different one appeared"
   * without logging pane content.
   *
   * Reporting only. It does NOT gate the Enter logic below: that path is
   * default-deny by design and its bounded fires plus give-up log are the
   * existing safety net. Holding the pane on every unrecognised modal would
   * be a behaviour change with its own blast radius (a harmless overlay would
   * stop stuck input ever being healed), and this ticket is about making the
   * state VISIBLE, not about keying more or less of it.
   */
  unclaimedDialogHash?: string;
  next: AgentInputState | undefined;
} {
  const threshold = config.stuckThresholdMs ?? STUCK_THRESHOLD_MS;
  const maxFires = config.maxEnterFires ?? MAX_ENTER_FIRES;

  // ENG-6017: dialogs first. While a recognised dialog overlays the pane,
  // an Enter would land on the dialog (the koda incident: the session-
  // feedback prompt ate the watchdog's single shot). Dismiss it and leave
  // the stuck-input state untouched — next cycle sees the clean pane and
  // the normal Enter logic resumes. Unknown overlays fall through to the
  // standard path (default-deny: we never key what we don't recognise;
  // worst case the bounded Enters fire and the give-up log surfaces it).
  const dialogAction = sweepDialogs(pane);
  if (dialogAction) {
    return { fire: false, dialog: dialogAction, next: prev };
  }

  // ENG-8213: the one case where "unrecognised, fall through" is NOT safe.
  // The usage-limit dialog is on screen but its safe option ("Stop and wait
  // for limit to reset") could not be located, so sweepDialogs deliberately
  // returned null. Falling through would let the bounded Enter fire onto
  // whichever row the cursor is on, and two of the three rows are billing
  // actions. Hold the pane and let the caller log it.
  if (isUnanswerableUsageLimitDialog(pane)) {
    return {
      fire: false,
      blockedDialog: 'usage-limit-choice-unrecognised-options',
      next: prev,
    };
  }

  // ENG-9006 criterion 3: nothing above claimed this pane, but a modal IS
  // focused on it. Report it and carry on — see the field's doc for why this
  // does not also gate the keying below.
  const unclaimedBlock = isUnclaimedConfirmDialog(pane)
    ? focusedConfirmDialogBlock(pane)
    : null;
  const unclaimedDialogHash = unclaimedBlock
    ? simpleTextHash(unclaimedBlock.join('\n'))
    : undefined;

  const inputText = extractInputBoxText(pane);
  if (!inputText) {
    return { fire: false, unclaimedDialogHash, next: undefined };
  }

  // ENG-6057: a lone "…" (U+2026) in the input box is a Claude Code render
  // artifact, not a stuck message. Fleet audit 2026-06-05: EVERY watchdog
  // fire in history was len=1 hash=2026 (the char code of …) — the watchdog
  // had only ever fired Enters at this phantom, burning retry budgets and
  // give-up counts on it (paige 2026-06-05) while never once firing on a
  // real message. Treat it as an empty box. (Trade-off: a genuine one-char
  // "…" channel message stuck unsubmitted would be ignored — vanishingly
  // rare against a 100%-phantom historical hit rate.)
  if (inputText === '…') {
    return { fire: false, unclaimedDialogHash, next: undefined };
  }

  // ENG-6057: the spinner gate is gone. It held fire while Claude was
  // actively working — which is exactly why the kylie 2026-06-05 stuck
  // inputs were never healed: the agent stayed busy with a long task, the
  // gate held on ~115 consecutive polls, and the stuck instruction outlived
  // the whole incident. Claude Code queues input typed during a running
  // turn, so an Enter while busy safely submits the queued text — the
  // original gate's own comment called the keystroke "harmless but noisy".
  // We keep isActivelyProcessing() as a log annotation only (see checkOne).

  const hash = simpleTextHash(inputText);
  if (!prev || prev.lastInputHash !== hash) {
    return {
      fire: false,
      unclaimedDialogHash,
      next: { lastInputHash: hash, firstSeenAt: now, fires: 0, lastFireAt: 0, gaveUpLogged: false },
    };
  }

  // ENG-6017: bounded retries replace the original single-shot `resolved`
  // flag. An Enter that lands while a dialog is mid-render (or is eaten by
  // a TUI mode hiccup) gets another chance, each spaced by the same stuck
  // threshold; after maxFires the watchdog surfaces a loud give-up line —
  // the signature monitoring hooks onto — instead of silently shrugging.
  if (prev.fires >= maxFires) {
    if (!prev.gaveUpLogged) {
      return {
        fire: false,
        gaveUp: true,
        unclaimedDialogHash,
        next: { ...prev, gaveUpLogged: true },
      };
    }
    return { fire: false, unclaimedDialogHash, next: prev };
  }

  const sinceLastAttempt = prev.fires === 0 ? now - prev.firstSeenAt : now - prev.lastFireAt;
  if (sinceLastAttempt < threshold) return { fire: false, unclaimedDialogHash, next: prev };

  return {
    fire: true,
    unclaimedDialogHash,
    next: { ...prev, fires: prev.fires + 1, lastFireAt: now },
  };
}

/**
 * Extract the contents of the Claude Code input box from a pane snapshot.
 * Returns null when the input box is empty or absent.
 *
 * The TUI bracketed input area looks like:
 *   ────────── agt-bob ──
 *   ❯ ping repro 2
 *   ──────────────────────
 *
 * We accept any line starting with `❯ ` whose previous non-empty line is a
 * row of `─` characters (the top divider) — that's robust to width changes
 * and to the optional ` agt-<codeName> ` label embedded in the divider.
 *
 * Exported for unit testing.
 */
export function extractInputBoxText(pane: string): string | null {
  const lines = pane.split('\n');
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i] ?? '';
    if (!line.startsWith(PROMPT_PREFIX)) continue;
    // Walk back to the most recent non-empty line; it must be a divider row.
    let j = i - 1;
    while (j >= 0 && (lines[j] ?? '').trim() === '') j--;
    if (j < 0) continue;
    if (!INPUT_BOX_DIVIDER.test((lines[j] ?? '').trim())) continue;
    const text = line.slice(PROMPT_PREFIX.length).trim();
    return text.length > 0 ? text : null;
  }
  return null;
}

/**
 * True when the pane shows a live spinner (`✻ Cogitating…`,
 * `✽ Tinkering… (36s · ↓ 2.1k tokens)`, etc.). Past tense forms like
 * `✻ Cogitated for 7s` mean the work has finished and we treat the agent
 * as idle.
 *
 * ENG-6057: annotation-only — this no longer gates the watchdog's fire
 * (that gate is why the kylie stuck inputs were never healed); it tags the
 * fire log with busy=true|false. The glyph class covers the spinner's
 * animation frames (✻ ✽ ✶ ✳ ✢), not just ✻ — the live kylie pane rendered
 * `✽ Tinkering…`, which the single-glyph match read as "no spinner".
 *
 * Exported for unit testing.
 */
const SPINNER_GLYPHS = ['✻', '✽', '✶', '✳', '✢'];

export function isActivelyProcessing(pane: string): boolean {
  // Search bottom-up for the most recent spinner line.
  const lines = pane.split('\n');
  for (let i = lines.length - 1; i >= 0; i--) {
    const line = (lines[i] ?? '').trim();
    if (!SPINNER_GLYPHS.some((g) => line.startsWith(g))) continue;
    // Past tense: "✻ Cogitated for 7s", "✻ Crunched for 51s" — work done.
    if (/\bfor\s+\d+s\s*$/.test(line)) return false;
    // Present participle: "✻ Cogitating…", "✽ Tinkering… (36s · ↓ 2.1k
    // tokens)" — still working. The trailing parenthetical is optional.
    if (/\b\w+ing[…\.]{0,3}(\s*\([^)]*\))?\s*$/i.test(line)) return true;
    // Ambiguous spinner line — don't treat as busy.
    return false;
  }
  return false;
}

/**
 * Run one watchdog pass over the given agents. Stateful: keeps an internal
 * map of per-agent input state across calls.
 */
export function checkChannelInputs(
  codeNames: readonly string[],
  io: WatchdogIo,
  config: WatchdogConfig = {},
  states: Map<string, AgentInputState> = sharedStates,
): void {
  const live = new Set(codeNames);
  for (const codeName of codeNames) {
    try {
      checkOne(codeName, io, config, states);
    } catch (err) {
      io.log(`[channel-input-watchdog] '${codeName}': ${(err as Error).message}`);
    }
  }
  // Drop state for agents that are no longer in scope.
  for (const key of [...states.keys()]) {
    if (!live.has(key)) states.delete(key);
  }
  // Keep give-up counters in sync with live agents as well — a stale entry
  // would mis-attribute an old count if a code name is reused (CodeRabbit
  // on PR #1764).
  for (const key of [...giveUpCounts.keys()]) {
    if (!live.has(key)) giveUpCounts.delete(key);
  }
  for (const key of [...unclaimedDialogCounts.keys()]) {
    if (!live.has(key)) unclaimedDialogCounts.delete(key);
  }
  for (const key of [...unclaimedDialogSeen.keys()]) {
    if (!live.has(key)) unclaimedDialogSeen.delete(key);
  }
  for (const key of [...usageLimitSignalled.keys()]) {
    if (!live.has(key)) usageLimitSignalled.delete(key);
  }
}

function checkOne(
  codeName: string,
  io: WatchdogIo,
  config: WatchdogConfig,
  states: Map<string, AgentInputState>,
): void {
  const pane = io.capturePane(codeName);
  if (!pane) {
    states.delete(codeName);
    return;
  }

  // ENG-4716: don't skip outright when a client is attached — operators
  // routinely keep a tmux client open just to monitor agents, and the
  // original blanket skip ate the steady-state case. Widen the stable-
  // buffer threshold instead. Active typing changes the buffer hash on
  // every keystroke, so the unchanged-hash gate already covers the
  // "don't fight the human" concern.
  const attached = io.isClientAttached(codeName);
  const effectiveConfig: WatchdogConfig = attached
    ? {
        ...config,
        stuckThresholdMs:
          config.attachedStuckThresholdMs ?? ATTACHED_STUCK_THRESHOLD_MS,
      }
    : config;

  const prev = states.get(codeName);
  const { fire, dialog, gaveUp, blockedDialog, unclaimedDialogHash, next } = decide(
    pane,
    prev,
    io.now(),
    effectiveConfig,
  );

  if (next === undefined) {
    states.delete(codeName);
    // ENG-6055: the input box cleared after at least one fire — the heal
    // landed (or a human submitted it). Distinct grep-able success token so
    // healed near-misses are countable separately from GIVING UP events; a
    // rising recovery rate is the early warning that a host is degrading
    // before it starts giving up.
    if (prev && prev.fires > 0) {
      io.log(
        `[channel-input-watchdog] '${codeName}': recovered after ${prev.fires} fire(s) — input submitted (input_hash=${prev.lastInputHash})`,
      );
    }
  } else {
    states.set(codeName, next);
  }

  // ENG-9006 criterion 3: an unclaimed focused modal, tracked to a threshold so
  // one loud line is emitted per occurrence rather than one per poll cycle.
  //
  // Placed here rather than in decide() on purpose: decide()'s state is keyed on
  // the INPUT BOX hash and is dropped whenever the box is empty — which is the
  // normal case when a modal is up. Tracking the dialog in that lifecycle would
  // reset firstSeenAt every cycle and the threshold would never be reached.
  trackUnclaimedDialog(codeName, unclaimedDialogHash, io, effectiveConfig);

  // ENG-9006: the cap is over (or was never on this pane) — forget that we
  // notified about it, so the NEXT cap notifies again.
  //
  // Found in self-review, not by a test: the once-per-cap gate is keyed on the
  // reset the modal names, and a modal that names none keys on a constant. So
  // without this clear, an agent capped twice with no reset text on either
  // occasion would notify the first time and be silent forever after — the
  // customer-facing silence this whole change exists to end, reintroduced by
  // the throttle meant to keep it quiet.
  const onUsageLimitModal =
    dialog?.kind === 'usage-limit-choice' ||
    blockedDialog === 'usage-limit-choice-unrecognised-options';
  if (!onUsageLimitModal) usageLimitSignalled.delete(codeName);

  if (dialog) {
    // ENG-6017: a recognised dialog is blocking the input box — dismiss it
    // instead of firing Enter into it. Next cycle re-evaluates the clean pane.
    io.log(
      `[channel-input-watchdog] '${codeName}': ${dialog.logMessage} (dialog was blocking the input box)`,
    );
    // ENG-9006: tell the humans BEFORE keying. Answering the modal is not the
    // same as being able to reply to them — on the auto-continue option the
    // agent idles until the reset, and on the stop option it idles until a
    // person intervenes. Either way somebody is waiting on a message that is
    // not coming, and this is the only place that knows it.
    if (dialog.kind === 'usage-limit-choice') {
      maybeSignalUsageLimit(codeName, pane, io);
    }
    io.sendKeys(codeName, dialog.keys, dialog.interKeyDelayMs);
    return;
  }

  if (blockedDialog) {
    // Same signal on the refuse path, and it matters MORE here: we are
    // deliberately sending nothing, so the pane stays blocked until a human
    // attaches to it. Silence toward the customer is the one thing that must
    // not also be true.
    if (blockedDialog === 'usage-limit-choice-unrecognised-options') {
      maybeSignalUsageLimit(codeName, pane, io);
    }
    // ENG-8213: loud, and deliberately sends NOTHING. A modal we recognise but
    // cannot safely answer needs a human, and the alternative (keying it
    // blindly) can spend money. Greppable so a fleet-wide occurrence is
    // countable.
    io.log(
      `[channel-input-watchdog] '${codeName}': BLOCKED DIALOG (${blockedDialog}) — refusing to send any key; needs a human to attach to the pane`,
    );
    return;
  }

  // Log hash + length only — channel input may contain PII / secrets, so
  // prod logging stays hash-only per the project's logging policy.
  const text = extractInputBoxText(pane) ?? '';
  const hash = next?.lastInputHash ?? simpleTextHash(text);

  if (gaveUp) {
    // ENG-6017: loud, single-shot per hash. This line is the fast-detection
    // signature for "input typed but unsubmittable" — the failure mode every
    // upstream health metric is blind to (koda 2026-06-04).
    const maxFires = effectiveConfig.maxEnterFires ?? MAX_ENTER_FIRES;
    io.log(
      `[channel-input-watchdog] '${codeName}': GIVING UP after ${maxFires} Enter attempts — input remains unsubmitted (input_hash=${hash}, len=${text.length})`,
    );
    // Count the event for the responsiveness probe (InputStuckGiveUps
    // metric) so the give-up reaches CloudWatch on the next probe cycle,
    // not just the local log.
    giveUpCounts.set(codeName, (giveUpCounts.get(codeName) ?? 0) + 1);
    // ENG-6058: persist the give-up so the channel servers can tell the
    // affected user(s) to resend — without this, the give-up is operator-
    // facing only and the human keeps talking to a wall.
    try {
      io.signalGiveUp?.(codeName);
    } catch (err) {
      io.log(
        `[channel-input-watchdog] '${codeName}': give-up signal write failed: ${(err as Error).message}`,
      );
    }
    return;
  }

  if (fire) {
    const maxFires = effectiveConfig.maxEnterFires ?? MAX_ENTER_FIRES;
    const attempt = next?.fires ?? 1;
    // ENG-6057: busy-state annotation — the spinner no longer gates the
    // fire (CC queues input typed during a running turn), but knowing
    // whether the agent was mid-turn when the heal landed is diagnostic
    // gold for the eaten-Enter root-cause hunt.
    const busy = isActivelyProcessing(pane);
    // ENG-6055: bare Enter first, disturb sequence (x→BSpace→Enter) for the
    // remaining budget — a bare retry repeats the exact keystroke the stuck
    // TUI state already ate (kylie 2026-06-05). The dialog sweep above has
    // already run this cycle, so the disturb char never lands on a
    // recognised overlay.
    const keys = selectFireKeys(attempt, effectiveConfig.healMode);
    if (keys.length === 1 && keys[0] === 'Enter') {
      io.log(
        `[channel-input-watchdog] '${codeName}': stuck channel input — firing Enter (attempt ${attempt}/${maxFires}, busy=${busy}, input_hash=${hash}, len=${text.length})`,
      );
      io.sendEnter(codeName);
    } else {
      io.log(
        `[channel-input-watchdog] '${codeName}': stuck channel input — escalating to disturb sequence ${keys.join('→')} (attempt ${attempt}/${maxFires}, busy=${busy}, input_hash=${hash}, len=${text.length})`,
      );
      io.sendKeys(codeName, keys, DISTURB_INTER_KEY_DELAY_MS);
    }
  }
}

const sharedStates = new Map<string, AgentInputState>();

// ENG-6017: per-agent count of give-up events since the last responsiveness
// probe drained them. Consumed by the manager's probe cycle and shipped to
// CloudWatch as `InputStuckGiveUps` — the fast-detection metric for the
// "typed but unsubmittable" failure mode (each event means a channel message
// sat in the input box through every bounded Enter retry).
const giveUpCounts = new Map<string, number>();

/**
 * Drain the give-up counter for one agent (returns the count since the last
 * drain). Read-and-reset so each probe cycle reports only new events.
 */
export function takeWatchdogGiveUpCount(codeName: string): number {
  const count = giveUpCounts.get(codeName) ?? 0;
  giveUpCounts.delete(codeName);
  return count;
}

/**
 * Re-credit drained give-up events (ENG-6037): called when the responsiveness
 * probe POST fails after takeWatchdogGiveUpCount() already drained the
 * counter, so the events surface on the next probe cycle instead of being
 * permanently lost. Adds to (not replaces) the live count — give-ups that
 * accrued while the POST was in flight are preserved, and only the
 * undelivered amount is re-added so a later success can't double-count.
 */
export function creditWatchdogGiveUpCount(codeName: string, count: number): void {
  if (count <= 0) return;
  giveUpCounts.set(codeName, (giveUpCounts.get(codeName) ?? 0) + count);
}

/**
 * ENG-9006: per-agent record of the usage-limit modal we have already told the
 * humans about, so one cap produces one notice rather than one per poll cycle.
 *
 * Keyed on the modal's reset hint (or a constant when it names none): a fresh
 * cap naming a NEW reset is a new event worth a new notice, while the same
 * modal sitting there for hours is not. The channel servers throttle again on
 * their side (a shared per-conversation window), so this is the cheap first
 * gate, not the only one.
 */
const usageLimitSignalled = new Map<string, string>();

function maybeSignalUsageLimit(codeName: string, pane: string, io: WatchdogIo): void {
  if (!io.signalUsageLimit) return;
  const hint = findUsageLimitResetHint(pane);
  const key = hint ?? '<no-reset-named>';
  if (usageLimitSignalled.get(codeName) === key) return;
  usageLimitSignalled.set(codeName, key);
  try {
    io.signalUsageLimit(codeName, hint);
    io.log(
      `[channel-input-watchdog] '${codeName}': usage-limit modal — signalled the channel servers so waiting people are told` +
        (hint ? ` (resets ${hint})` : ' (modal named no reset)'),
    );
  } catch (err) {
    io.log(
      `[channel-input-watchdog] '${codeName}': usage-limit signal write failed: ${(err as Error).message}`,
    );
  }
}

/**
 * ENG-9006 criterion 3: persistence tracking for an unclaimed focused modal.
 *
 * Emits at most ONE line per distinct unclaimed modal. A pane that changes to a
 * different unclaimed modal restarts the clock and can alert again; the same
 * one sitting there for an hour alerts once. Hash-only — the block itself is
 * never logged, since a pane can carry customer message text (the ENG-9006
 * capture had four customer Telegram messages in it).
 */
function trackUnclaimedDialog(
  codeName: string,
  hash: string | undefined,
  io: WatchdogIo,
  config: WatchdogConfig,
): void {
  if (!hash) {
    // Modal gone (or claimed this cycle) — reset so a future one starts clean.
    unclaimedDialogSeen.delete(codeName);
    return;
  }
  const thresholdMs = config.unclaimedDialogAlertMs ?? UNCLAIMED_DIALOG_ALERT_MS;
  const now = io.now();
  const prev = unclaimedDialogSeen.get(codeName);
  if (!prev || prev.hash !== hash) {
    unclaimedDialogSeen.set(codeName, { hash, firstSeenAt: now, alerted: false });
    return;
  }
  if (prev.alerted) return;
  const ageMs = now - prev.firstSeenAt;
  if (ageMs < thresholdMs) return;
  unclaimedDialogSeen.set(codeName, { ...prev, alerted: true });
  unclaimedDialogCounts.set(codeName, (unclaimedDialogCounts.get(codeName) ?? 0) + 1);
  // Greppable, and deliberately says what it does NOT know. The point of this
  // line is that the next rewording of a Claude Code modal is discovered by the
  // fleet rather than by someone opening a screenshot.
  io.log(
    `[channel-input-watchdog] '${codeName}': UNCLAIMED DIALOG on pane for ${Math.round(
      ageMs / 1000,
    )}s — a modal is focused and no handler in claude-dialogs.ts recognises it ` +
      `(dialog_hash=${hash}); sending nothing. Claude Code has likely reworded a ` +
      `dialog: attach to the pane, then add the new option labels.`,
  );
}

/**
 * ENG-9006 criterion 3. Per-agent count of UNCLAIMED focused dialogs that
 * outlived UNCLAIMED_DIALOG_ALERT_MS since the last probe drained them, and the
 * per-agent tracking that decides when one has.
 *
 * Same read-and-reset shape as `giveUpCounts` above, deliberately: that counter
 * is already carried to CloudWatch by the responsiveness probe as
 * `InputStuckGiveUps`, so this one rides an alerting path that exists and is
 * proven rather than inventing a second one.
 *
 * What it measures: "a modal is focused on this agent's pane, no handler in
 * claude-dialogs.ts claimed it, and it is still there two minutes later." That
 * is the state ENG-8213 and ENG-9006 were both found in, both times by a human
 * looking at a screenshot.
 */
const unclaimedDialogCounts = new Map<string, number>();
const unclaimedDialogSeen = new Map<
  string,
  { hash: string; firstSeenAt: number; alerted: boolean }
>();

/**
 * Drain the unclaimed-dialog counter for one agent (count since last drain).
 */
export function takeWatchdogUnclaimedDialogCount(codeName: string): number {
  const count = unclaimedDialogCounts.get(codeName) ?? 0;
  unclaimedDialogCounts.delete(codeName);
  return count;
}

/**
 * Re-credit drained unclaimed-dialog events, for the same reason
 * creditWatchdogGiveUpCount exists (ENG-6037): a failed probe POST must not
 * permanently lose the events it already drained.
 */
export function creditWatchdogUnclaimedDialogCount(codeName: string, count: number): void {
  if (count <= 0) return;
  unclaimedDialogCounts.set(codeName, (unclaimedDialogCounts.get(codeName) ?? 0) + count);
}

/** Test seam — clear the singleton map between tests. */
export function _resetSharedStatesForTests(): void {
  sharedStates.clear();
  giveUpCounts.clear();
  unclaimedDialogCounts.clear();
  unclaimedDialogSeen.clear();
  usageLimitSignalled.clear();
}
